import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { hostUrl } from "../../../utils/globals";
import OtpInput from "react-otp-input";
import { setEmbedCredentials } from "../embedSlices/embedAuthSlice";
import googleLogo from "../../../assets/channel_images/google_logo.svg";
import { getCsrfToken } from "../../../services/csrfToken";
import StorageManager from "./../utility/storage_manager";
import { domainUrl } from "./../../../utils/globals";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchMyData } from "../../../redux/slices/myDataSlice";

const EmbedAuthPage = ({ initialEmail = "" }) => {
  const [isLogin, setIsLogin] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState(initialEmail);
  const [otpBackend, setOtpBackend] = useState("");
  const [otp, setOtp] = useState("");
  const [newError, setNewError] = useState("");
  const [channelId, setChannelId] = useState("");
  const [showOtp, setShowOtp] = useState("");
  const dispatch = useDispatch();
  // Business branding (logo + display name) so the sign-in screen feels like
  // the business's own, not a generic Channels page.
  const branding = useSelector((state) => state.embedHome?.branding) || {};
  const brandName = branding.displayName || "";
  const csrfToken = getCsrfToken();
  const [currentDomain, setCurrentDomain] = useState("");
  const location = useLocation();
  const [redirectUrl, setRedirectUrl] = useState("");
  const [redirectDomain, setRedirectDomain] = useState("");
  const navigate = useNavigate();

  // Only offer the sign-in method the business configured. jwt/trusted (or an
  // unknown mode) fall back to showing both so the user is never locked out.
  const authMode = localStorage.getItem("embedAuthMode") || "";
  const otpEnabled = authMode !== "google";
  const googleEnabled = authMode !== "otp";

  // const [channelId, setChannelId] = useState("");

  useEffect(() => {
    const data = localStorage.getItem("embedData");
    if (data) {
      const dataId = JSON.parse(data);
      setCurrentDomain(dataId.domain);
    }
  }, []);

  const clearData = () => {
    setFullName("");
    setEmail("");
    setOtp("");
    setOtpBackend("");
    setNewError("");
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirectParam = searchParams.get("redirect");
    if (redirectParam) {
      let normalizedRedirect = redirectParam;
      normalizedRedirect = normalizedRedirect.replace(
        /(?:\/embed\/channels)+/g,
        ""
      );
      normalizedRedirect = `/embed/channels${
        normalizedRedirect.startsWith("/") ? "" : "/"
      }${normalizedRedirect}`;
      setRedirectUrl(normalizedRedirect);
      const match = redirectParam.match(/\/channel\/([^/?]+)/);
      if (match && match[1]) {
        setChannelId(match[1]);
      }
    }
  }, [location.search]);

  const handleSendOtp = async () => {
    setNewError("");
    try {
      const userData = {
        name: fullName.trim(),
        email: email.trim(),
        domain: currentDomain,
      };
      await axios
        .post(`${hostUrl}/api/login/embed`, userData, {
          headers: {
            "X-CSRF-Token": csrfToken,
          },
          withCredentials: true,
        })
        .then((response) => {
          if (response.data.success) {
            setShowOtp(true);
          } else {
            setNewError(response.data.message);
          }
        });
    } catch (error) {
      setNewError("Error sending code. Please try again");
    }
  };

  const handleChangeOtp = async (value) => {
    setOtp(value);
    if (value.length === 6) {
      setNewError("");
      const userData = {
        name: fullName.trim(),
        email: email.trim(),
        domain: currentDomain,
        channel: channelId,
        otp: value,
      };
      try {
        await axios
          .post(`${hostUrl}/api/verify/login/embed`, userData, {
            headers: {
              "X-CSRF-Token": csrfToken,
            },
            // withCredentials: true,
          })
          .then((response) => {
            if (response.data.success) {
              const user = response.data.user;
              const partialUser = {
                name: user.name,
                username: user.username,
                email: user.email,
                contact: user.contact,
                whatsapp_number: user.whatsapp_number,
                _id: user._id,
              };
              localStorage.setItem("user", JSON.stringify(partialUser));
              localStorage.setItem("auth-token", response.data.token);
              dispatch(
                setEmbedCredentials({
                  user: response.data.user,
                  token: response.data.token,
                })
              );
              clearData();
              dispatch(fetchMyData());
              if (redirectUrl !== "") {
                navigate(`${redirectUrl}`, {
                  replace: true,
                });
              } else {
                const data = localStorage.getItem("embedFetchedData");
                if (data) {
                  const dataId = JSON.parse(data);
                  navigate(
                    `/embed/channels/user/${dataId.username}/channel/${dataId.selectedChannel}`,
                    {
                      replace: true,
                    }
                  );
                }
              }
            } else {
              setNewError(response.data.message);
            }
          });
      } catch (error) {
        setNewError("Error verifying code. Please try again");
      }
    }
  };

  const handleGoogleAuthPopup = () => {
    const data = StorageManager.getItem("embedData");
    const embedData = JSON.parse(data);
    const originalDomain = embedData.domain;

    setNewError("");
    const authUrl = `https://channels.social/embed/google-auth/login?domain=${window.location.origin}&hostDomain=${originalDomain}&channel=${channelId}`;
    const popup = window.open(
      authUrl,
      "_blank",
      "width=500,height=600,left=100,top=100"
    );
    const handleMessage = (event) => {
      const allowedOrigin = "https://channels.social";

      if (event.origin !== allowedOrigin) return;

      const { success, user, token } = event.data;
      if (success) {
        const partialUser = {
          name: user.name,
          _id: user._id,
          email: user.email,
          username: user.username,
          contact: user.contact,
          whatsapp_number: user.whatsapp_number,
        };

        localStorage.setItem("auth-token", token);
        localStorage.setItem("user", JSON.stringify(partialUser));
        dispatch(
          setEmbedCredentials({
            user,
            token,
          })
        );
        dispatch(fetchMyData());
        if (redirectUrl !== "") {
          navigate(`${redirectUrl}`, {
            replace: true,
          });
        } else {
          const data = localStorage.getItem("embedFetchedData");
          if (data) {
            const dataId = JSON.parse(data);
            navigate(
              `/embed/channels/user/${dataId.username}/channel/${dataId.selectedChannel}`,
              {
                replace: true,
              }
            );
          }
        }
      }
      window.removeEventListener("message", handleMessage);
    };

    window.addEventListener("message", handleMessage);
  };

  const isDark = document.documentElement.classList.contains("dark");

  return (
    <div
      className="flex items-center justify-center h-full w-full px-4 py-8 bg-theme-secondaryBackground"
      style={{
        backgroundImage:
          "radial-gradient(80% 60% at 50% -10%, rgba(127,127,127,0.10), transparent 70%)",
      }}
    >
      <div className="w-full max-w-[370px] flex flex-col items-center rounded-2xl border border-theme-chatDivider bg-theme-tertiaryBackground p-7 shadow-xl">
        {branding.logo ? (
          <img
            src={branding.logo}
            alt={brandName || "brand"}
            className="mb-4 h-16 w-16 rounded-2xl object-cover ring-1 ring-theme-chatDivider"
          />
        ) : (
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-theme-secondaryBackground text-2xl font-semibold text-theme-secondaryText ring-1 ring-theme-chatDivider">
            {(brandName || "C").charAt(0).toUpperCase()}
          </div>
        )}
        <h3 className="text-xl sm:text-2xl text-center font-semibold text-theme-secondaryText font-inter">
          {brandName ? `Sign in to ${brandName}` : "Sign in"}
        </h3>
        <p className="mt-1.5 mb-6 text-center text-sm font-light text-theme-placeholder font-inter">
          {brandName
            ? `Join the conversation in the ${brandName} community.`
            : "Join the conversation."}
        </p>

        <div className="w-full space-y-3">
          {otpEnabled && !showOtp && (
            <>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-lg border border-theme-chatDivider bg-theme-secondaryBackground px-3.5 py-2.5 text-sm font-light font-inter text-theme-secondaryText outline-none placeholder:text-theme-placeholder focus:border-theme-secondaryText"
                placeholder="Full name (optional)"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-theme-chatDivider bg-theme-secondaryBackground px-3.5 py-2.5 text-sm font-light font-inter text-theme-secondaryText outline-none placeholder:text-theme-placeholder focus:border-theme-secondaryText"
                placeholder="Email address"
              />
              <button
                onClick={handleSendOtp}
                disabled={!email.trim()}
                className="w-full rounded-lg bg-theme-secondaryText py-2.5 text-sm font-medium text-theme-primaryBackground transition-opacity disabled:opacity-50"
              >
                Send code
              </button>
            </>
          )}

          {otpEnabled && showOtp && (
            <div className="flex flex-col items-center">
              <p className="mb-3 text-center text-xs font-light text-theme-placeholder">
                Enter the 6-digit code sent to {email}
              </p>
              <OtpInput
                value={otp}
                onChange={handleChangeOtp}
                numInputs={6}
                renderSeparator={<span className="w-2 xs:w-3"></span>}
                renderInput={(props) => (
                  <input
                    {...props}
                    style={{
                      width: "36px",
                      height: "40px",
                      borderRadius: "6px",
                      border: isDark ? "1px solid #5a5a5a" : "1px solid #edecea",
                      backgroundColor: isDark ? "#2B2930" : "#e8e8e8",
                      textAlign: "center",
                      color: isDark ? "#ffffff" : "#202020",
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                )}
              />
              <button
                onClick={handleSendOtp}
                className="mt-3 text-xs font-light text-theme-placeholder underline"
              >
                Resend code
              </button>
            </div>
          )}
        </div>

        {newError && (
          <div className="mt-3 w-full text-center text-theme-error font-light text-sm">
            {newError}
          </div>
        )}

        {otpEnabled && googleEnabled && (
          <div className="my-4 flex w-full items-center gap-3">
            <span className="h-px flex-1 bg-theme-chatDivider" />
            <span className="text-xs text-theme-placeholder">or</span>
            <span className="h-px flex-1 bg-theme-chatDivider" />
          </div>
        )}

        {googleEnabled && (
          <button
            onClick={handleGoogleAuthPopup}
            className={`${
              otpEnabled ? "" : "mt-2"
            } w-full flex items-center justify-center gap-2 rounded-lg border border-theme-chatDivider bg-theme-secondaryBackground py-2.5 text-sm font-medium text-theme-secondaryText`}
          >
            <img src={googleLogo} alt="Google logo" className="w-5 h-5" />
            Continue with Google
          </button>
        )}
      </div>
    </div>
  );
};

export default EmbedAuthPage;
